import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import { ArrowLeft, CheckCircle, Clock, Loader2, Sparkles, Trophy } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VocabularyExerciseDuolingo = () => {
  const navigate = useNavigate();
  const { level, theme } = useParams();
  const [session, setSession] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [timeLeft, setTimeLeft] = useState(600);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  
  useEffect(() => {
    startExercise();
  }, []);
  
  useEffect(() => {
    if (session && timeLeft > 0) {
      const timer = setInterval(() => {
        setTimeLeft(prev => prev - 1);
      }, 1000);
      return () => clearInterval(timer);
    } else if (timeLeft === 0) {
      handleSubmit();
    }
  }, [session, timeLeft]);
  
  const startExercise = async () => {
    try {
      const response = await axios.post(
        `${API}/exercises/vocabulary/start?theme=${theme}&level=${level}&count=10`,
        {},
        { withCredentials: true }
      );
      setSession(response.data);
      setAnswers(new Array(response.data.exercises.length).fill(null));
    } catch (error) {
      toast.error('Egzersiz başlatılamadı');
      navigate('/exercises');
    } finally {
      setLoading(false);
    }
  };
  
  const handleAnswer = (answer) => {
    setSelectedAnswer(answer);
    const newAnswers = [...answers];
    newAnswers[currentIndex] = answer;
    setAnswers(newAnswers);
    
    setTimeout(() => {
      if (currentIndex < session.exercises.length - 1) {
        setCurrentIndex(currentIndex + 1);
        setSelectedAnswer(null);
      }
    }, 500);
  };
  
  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const formattedAnswers = answers.map((answer, idx) => ({
        exercise_id: idx,
        answer: answer !== null ? answer : 0
      }));
      
      await axios.post(
        `${API}/exercises/submit`,
        {
          session_id: session.session_id,
          answers: formattedAnswers
        },
        { withCredentials: true }
      );
      
      navigate(`/exercises/results/${session.session_id}`);
    } catch (error) {
      toast.error('Gönderim başarısız');
    } finally {
      setSubmitting(false);
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50">
        <div className="text-center">
          <div className="relative">
            <div className="w-24 h-24 border-8 border-purple-200 border-t-purple-600 rounded-full animate-spin mx-auto"></div>
            <div className="absolute inset-0 flex items-center justify-center">
              <Sparkles className="w-12 h-12 text-purple-600 animate-pulse" />
            </div>
          </div>
          <p className="mt-6 text-xl font-bold text-gray-700 animate-pulse">Kelimeler hazırlanıyor...</p>
          <p className="text-sm text-gray-500">💬 Seviyene uygun kelimeler seçiliyor!</p>
        </div>
      </div>
    );
  }
  
  if (!session) return null;
  
  const currentExercise = session.exercises[currentIndex];
  const answeredCount = answers.filter(a => a !== null).length;
  const progressPercentage = (answeredCount / session.exercises.length) * 100;
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50 py-8">
      <div className="container mx-auto px-4 max-w-3xl">
        <div className="flex items-center justify-between mb-6">
          <Button
            variant="outline"
            onClick={() => navigate('/exercises')}
            className="border-2 border-gray-300 hover:border-gray-500"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Geri
          </Button>
          
          <div className="flex items-center gap-2 px-4 py-2 bg-orange-100 border-2 border-orange-300 rounded-full">
            <Clock className="w-5 h-5 text-orange-600" />
            <span className="text-lg font-black text-orange-600">
              {Math.floor(timeLeft / 60)}:{(timeLeft % 60).toString().padStart(2, '0')}
            </span>
          </div>
        </div>
        
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-bold text-gray-600">İlerleme</span>
            <span className="text-sm font-bold text-purple-600">{answeredCount}/{session.exercises.length}</span>
          </div>
          <Progress value={progressPercentage} className="h-3 bg-gray-200" />
        </div>
        
        <Card className="border-4 border-white bg-white shadow-2xl mb-6">
          <CardContent className="p-8">
            <div className="mb-6">
              <div className="inline-block px-4 py-2 bg-purple-100 border-2 border-purple-300 rounded-full mb-4">
                <span className="text-sm font-black text-purple-600">Soru {currentIndex + 1}/{session.exercises.length}</span>
              </div>
              <h2 className="text-3xl font-black text-gray-900 mb-2">{currentExercise.question}</h2>
            </div>
            
            <div className="space-y-3">
              {currentExercise.options.map((option, idx) => {
                const isSelected = answers[currentIndex] === idx;
                const isCurrentlyClicked = selectedAnswer === idx;
                
                return (
                  <button
                    key={idx}
                    onClick={() => handleAnswer(idx)}
                    disabled={submitting}
                    className={`w-full p-6 rounded-2xl border-4 transition-all text-left font-bold text-lg ${
                      isSelected
                        ? 'border-purple-500 bg-purple-100 shadow-xl scale-105'
                        : 'border-gray-200 hover:border-purple-300 hover:bg-purple-50 hover:scale-102'
                    } ${
                      isCurrentlyClicked ? 'animate-pulse' : ''
                    }`}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-8 h-8 rounded-full border-4 flex items-center justify-center shrink-0 ${
                        isSelected
                          ? 'border-purple-500 bg-purple-500'
                          : 'border-gray-300 bg-white'
                      }`}>
                        {isSelected && <CheckCircle className="w-5 h-5 text-white" />}
                      </div>
                      <span className="flex-1">{option}</span>
                    </div>
                  </button>
                );
              })}
            </div>
          </CardContent>
        </Card>
        
        <div className="flex justify-center gap-2 mb-6">
          {session.exercises.map((_, idx) => (
            <button
              key={idx}
              onClick={() => setCurrentIndex(idx)}
              className={`transition-all ${
                idx === currentIndex
                  ? 'w-4 h-4 bg-purple-500 rounded-full'
                  : answers[idx] !== null
                  ? 'w-3 h-3 bg-green-500 rounded-full'
                  : 'w-3 h-3 bg-gray-300 rounded-full'
              }`}
            />
          ))}
        </div>
        
        <div className="flex justify-center gap-4">
          {currentIndex === session.exercises.length - 1 ? (
            <Button
              onClick={handleSubmit}
              disabled={submitting}
              size="lg"
              className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-black text-xl px-12 py-6 rounded-2xl shadow-2xl"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-6 h-6 mr-2 animate-spin" />
                  Gönderiliyor...
                </>
              ) : (
                <>
                  <Trophy className="w-6 h-6 mr-2" />
                  Tamamla! 🎉
                </>
              )}
            </Button>
          ) : (
            <Button
              onClick={() => {
                setCurrentIndex(currentIndex + 1);
                setSelectedAnswer(null);
              }}
              size="lg"
              className="bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white font-black text-xl px-12 py-6 rounded-2xl shadow-2xl"
            >
              Sonraki →
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default VocabularyExerciseDuolingo;